import { styled } from "goober";
import { useMemo } from "react";
import { STEP_TESTS_FEATURE_FLAG } from "../../shared/featureFlagNames";
import { queries } from "../../shared/queries";
import type { Slug } from "../../shared/slug";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { useQuery } from "../hooks/useQuery";
import type { StepTestFixtureSetupRequest } from "../hooks/useStepTestPin";
import type { Connector } from "../lib/connector";
import type { ParsedStep } from "../lib/parseCommitData";
import { makeUrl, routes, useCurrentUrl } from "../lib/urls";
import { Icon } from "./Icon";
import { Link } from "./Link";
import { Text } from "./Text";
import type { BranchData, ElementSnippet } from "./Workflow";
import { WorkflowBrowserPanel } from "./WorkflowBrowserPanel";
import { WorkflowLinkGallery } from "./WorkflowLinkGallery";
import { WorkflowMonitorPanel } from "./WorkflowMonitorPanel";
import { WorkflowReadmePanel } from "./WorkflowReadmePanel";
import { WorkflowRunsPanel } from "./WorkflowRunsPanel";
import { resolveWorkflowTabOrder, type Tab, VALID_TABS } from "./WorkflowTabs";
import { WorkflowTestingPanel } from "./WorkflowTestingPanel";

function parseTab(raw: string | undefined, fallback: Tab): Tab {
  return raw && VALID_TABS.has(raw) ? (raw as Tab) : fallback;
}

type Props = {
  workflowId: string;
  branchId: string;
  spaceSlug: Slug;
  spaceId: string;
  branch: BranchData;
  canWrite: boolean;
  readme: string | null;
  steps: ReadonlyArray<ParsedStep>;
  allConnectors: ReadonlyArray<Connector>;
  inspectMode: boolean;
  onInspectModeChange: (mode: boolean) => void;
  onElementSelect: (
    element: Omit<ElementSnippet, "stepName" | "color">
  ) => void;
  onElementDeselect: (index: number) => void;
  elementSnippets: readonly ElementSnippet[];
  hoveredCanonicalId: string | null;
  onStepHover: (canonicalId: string | null) => void;
  onStepsHover: (canonicalIds: ReadonlyArray<string> | null) => void;
  onRunsTabStepsChange: (canonicalIds: ReadonlyArray<string> | null) => void;
  onAskWriteReadme: () => void;
  onInvestigateFailure: (params: { stepDir: string; testName: string }) => void;
  onAskSetupStepTest: (request: StepTestFixtureSetupRequest) => void;
};

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  height: 0;
  flex: 1;
`;

const HeaderRow = styled("div")`
  padding: 12px;
  flex-shrink: 0;
`;

const TabStrip = styled("div")`
  display: flex;
  gap: 8px;
  overflow: hidden;
`;

const BodyArea = styled("div")`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 0 12px;
`;

const BodyCard = styled("div")`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--surface-secondary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  overflow: hidden;

  &[data-flat="true"] {
    background: transparent;
    border: none;
    border-radius: 0;
    overflow: visible;
  }
`;

const TabLink = styled(Link)`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  text-decoration: none;
  outline: none;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 24px;
  cursor: pointer;
  color: var(--text-secondary);

  --tab-hover-background:
    linear-gradient(var(--alpha4)), var(--surface-primary);
  --tab-background: var(--surface-primary);
  background: var(--tab-background);

  &[data-active="true"],
  &:hover {
    --tab-background: var(--tab-hover-background);
    color: var(--text-primary);
  }
`;

export function WorkflowOverview({
  workflowId,
  branchId,
  spaceSlug,
  spaceId,
  branch,
  canWrite,
  readme,
  steps,
  allConnectors,
  inspectMode,
  onInspectModeChange,
  onElementSelect,
  onElementDeselect,
  elementSnippets,
  hoveredCanonicalId,
  onStepHover,
  onStepsHover,
  onRunsTabStepsChange,
  onAskWriteReadme,
  onInvestigateFailure,
  onAskSetupStepTest,
}: Props) {
  const { tab: rawTab } = useCurrentUrl(routes.space.workflow);
  const stepTestsEnabled = useFeatureFlag(STEP_TESTS_FEATURE_FLAG);

  const [preferences] = useQuery(queries.userPreferences());
  const orderedTabs = useMemo(
    () =>
      resolveWorkflowTabOrder(preferences?.workflowTabOrder).filter(
        (t) => stepTestsEnabled || t.key !== "testing"
      ),
    [preferences?.workflowTabOrder, stepTestsEnabled]
  );

  const fallbackTab = readme?.trim()
    ? "readme"
    : (orderedTabs[0]?.key ?? "monitor");
  const parsedTab = parseTab(rawTab, fallbackTab);
  const tab =
    parsedTab === "testing" && !stepTestsEnabled ? fallbackTab : parsedTab;

  const workflowConnectors = useMemo(() => {
    const ids = new Set<string>();
    for (const s of steps) {
      for (const ca of s.connectedApps) {
        if (ca.connectedAppId) ids.add(ca.connectedAppId);
      }
    }
    return allConnectors.filter((ca) => ids.has(ca.id));
  }, [steps, allConnectors]);

  return (
    <Container>
      <HeaderRow>
        <TabStrip>
          {orderedTabs.map((t) => (
            <TabLink
              key={t.key}
              to={makeUrl(routes.space.workflow, { tab: t.key })}
              data-active={tab === t.key ? "true" : undefined}
            >
              <Icon name={t.icon} size={16} />
              <Text variant="text-small-md">{t.label}</Text>
            </TabLink>
          ))}
        </TabStrip>
      </HeaderRow>
      <BodyArea>
        <BodyCard
          data-flat={
            tab === "browser" || tab === "monitor" || tab === "runs"
              ? "true"
              : undefined
          }
        >
          {tab === "readme" && (
            <WorkflowReadmePanel
              readme={readme}
              canWrite={canWrite}
              stepNames={steps.map((s) => s.name)}
              onAskWriteReadme={onAskWriteReadme}
            />
          )}
          {tab === "links" && (
            <WorkflowLinkGallery
              workflowId={workflowId}
              branchId={branchId}
              steps={steps}
              onStepHover={onStepHover}
            />
          )}
          {tab === "browser" && (
            <WorkflowBrowserPanel
              branch={branch}
              spaceSlug={spaceSlug}
              spaceId={spaceId}
              steps={steps}
              inspectMode={inspectMode}
              onInspectModeChange={onInspectModeChange}
              onElementSelect={onElementSelect}
              onElementDeselect={onElementDeselect}
              elementSnippets={elementSnippets}
            />
          )}
          {tab === "monitor" && (
            <WorkflowMonitorPanel
              workflowId={workflowId}
              branchId={branchId}
              steps={steps}
              connectors={workflowConnectors}
              onStepsHover={onStepsHover}
            />
          )}
          {tab === "testing" && (
            <WorkflowTestingPanel
              workflowId={workflowId}
              branchId={branchId}
              steps={steps}
              onInvestigateFailure={onInvestigateFailure}
              onAskSetupStepTest={onAskSetupStepTest}
            />
          )}
          {tab === "runs" && (
            <WorkflowRunsPanel
              branchId={branchId}
              steps={steps}
              hoveredCanonicalId={hoveredCanonicalId}
              onStepHover={onStepHover}
              onActiveRunStepsChange={onRunsTabStepsChange}
            />
          )}
        </BodyCard>
      </BodyArea>
    </Container>
  );
}
